import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import {
  FileUpload,
  FileUploadHeaderTemplateOptions,
  FileUploadSelectEvent,
  FileUploadHandlerEvent,
} from "primereact/fileupload";
import { useFormik } from "formik";
import { Timeline } from "primereact/timeline";
import { Panel } from "primereact/panel";
import { useNavigate } from "react-router-dom";
import {
  initialKycValues,
  KycValues,
} from "../../validation/KycValidation";
import { DocumentFile, DocumentMetadata } from "../../models/Document";
import { DocumentService } from "../../services/documentService";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import moment from "moment";

type Props = {};

const BusinessKycForm: React.FC<Props> = () => {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [totalSize, setTotalSize] = useState(0);
  const [filePreviews, setFilePreviews] = useState<{
    [key: string]: string | undefined;
  }>({});
  const [fileCanceled, setFileCanceled] = useState<{ [key: string]: boolean }>(
      {}
  );
  const [fileNames, setFileNames] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const { currentUser } = useSelector((state: RootState) => state.auth);

  const [steps, setSteps] = useState<any[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);

  const fetchKycDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await DocumentService.getKycEnterprise(); // Récupère tous les documents KYC
      setAllDocuments(response.data);
    } catch (err) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Error",
        detail: "Failed to fetch required documents.",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchKycDocuments();
  }, [currentUser]);

  const mapApiDocumentsToSteps = (documents: any[]) => {
    // Filtrer : on garde les documents qui ne sont PAS réservés aux particuliers
    const filtered = documents.filter((doc) => !doc.forCustomerOnly);

    return filtered.flatMap((doc) => {
      if (doc.doc_type_code === "NATIONAL_ID") {
        return [
          {
            status: `${doc.name} (FRONT)`,
            icon: "pi pi-file",
            iconColor: "#274081",
            uploadKey: `${doc.doc_type_code}_FRONT`,
            progress: 0,
            isCollapsed: true,
            required: !doc.isOptional,
            validated: true,
            requiresExpiry: doc.requiresExpiry || false,
            requiresNumber: doc.requiresNumber || false,
            expiryDate: null,
            documentNumber: "",
          },
          {
            status: `${doc.name} (BACK)`,
            icon: "pi pi-file",
            iconColor: "#274081",
            uploadKey: `${doc.doc_type_code}_BACK`,
            progress: 0,
            isCollapsed: true,
            required: !doc.isOptional,
            validated: true,
            requiresExpiry: false,
            requiresNumber: false,
            expiryDate: null,
            documentNumber: "",
          },
        ];
      } else {
        return {
          status: doc.forEnterpriseOnly ? `${doc.name} *` : doc.name, // astérisque si spécifique entreprise (décoratif)
          icon: "pi pi-file",
          iconColor: "#274081",
          uploadKey: doc.doc_type_code,
          progress: 0,
          isCollapsed: true,
          required: !doc.isOptional,
          validated: true,
          requiresExpiry: doc.requiresExpiry || false,
          requiresNumber: doc.requiresNumber || false,
          expiryDate: null,
          documentNumber: "",
        };
      }
    });
  };

  useEffect(() => {
    if (allDocuments.length > 0) {
      const mappedSteps = mapApiDocumentsToSteps(allDocuments);
      setSteps(mappedSteps);
    }
  }, [allDocuments]);

  const togglePanel = (index: number) => {
    setSteps((prevSteps) =>
        prevSteps.map((step, i) =>
            i === index ? { ...step, isCollapsed: !step.isCollapsed } : step
        )
    );
  };

  const handleFileSelect = (e: FileUploadSelectEvent, uploadKey: string) => {
    const file = e.files && e.files[0];

    if (!file) {
      toastRef.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: "No file selected.",
        life: 3000,
      });
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      toastRef.current?.show({
        severity: "error",
        summary: "Invalid File Type",
        detail: "Only PDF, JPEG, JPG, and PNG files are allowed.",
        life: 3000,
      });
      e.files.length = 0;
      return;
    }

    if (file.size > 4000000) {
      toastRef.current?.show({
        severity: "error",
        summary: "File Too Large",
        detail: "Maximum file size is 4MB.",
        life: 3000,
      });
      e.files.length = 0;
      return;
    }

    let _totalSize = totalSize;
    _totalSize += file.size || 0;
    setTotalSize(_totalSize);

    const filePreview =
        file?.type?.startsWith("image/") || file?.type === "application/pdf"
            ? URL.createObjectURL(file)
            : undefined;

    setFilePreviews((prev) => ({ ...prev, [uploadKey]: filePreview }));
    setFileCanceled((prev) => ({ ...prev, [uploadKey]: false }));
    setFileNames((prev) => ({ ...prev, [uploadKey]: file.name }));
  };

  const handleFileUpload = (e: FileUploadHandlerEvent, uploadKey: string) => {
    try {
      let _totalSize = 0;
      e.files.forEach((file) => {
        _totalSize += file.size || 0;
      });

      setTotalSize(_totalSize);
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: "File uploaded successfully.",
        life: 3000,
      });

      const file = e.files[0];
      const filePreview =
          file.type.startsWith("image/") || file.type === "application/pdf"
              ? URL.createObjectURL(file)
              : undefined;
      setFilePreviews((prev) => ({ ...prev, [uploadKey]: filePreview }));
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: "An error occurred while uploading the file.",
        life: 3000,
      });
    }
  };

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { className, chooseButton, cancelButton } = options;
    return (
        <div
            className={className}
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {React.cloneElement(chooseButton, {
              style: {
                backgroundColor: "#274081",
                color: "white",
                width: "120px",
                height: "35px",
              },
            })}
            {React.cloneElement(cancelButton, {
              style: { color: "red", width: "120px" },
            })}
          </div>
        </div>
    );
  };

  const handleFileClear = (uploadKey: string) => {
    setFileCanceled((prev) => ({ ...prev, [uploadKey]: true }));
    setFilePreviews((prev) => ({ ...prev, [uploadKey]: undefined }));
    setFileNames((prev) => ({ ...prev, [uploadKey]: "" }));
  };

  const isStepCoreValid = (step: any) => {
    if (!filePreviews[step.uploadKey]) {
      return false;
    }
    if (step.requiresNumber && !step.documentNumber?.trim()) {
      return false;
    }
    if (step.requiresExpiry) {
      if (!step.expiryDate) {
        return false;
      }
      const expiryDate = new Date(step.expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        return false;
      }
    }
    return true;
  };

  const isStepValid = (step: any) => {
    if (step.uploadKey.endsWith("_BACK")) {
      const frontKey = step.uploadKey.replace("_BACK", "_FRONT");
      const frontStep = steps.find((s) => s.uploadKey === frontKey);
      return !!filePreviews[step.uploadKey] && (frontStep ? isStepCoreValid(frontStep) : true);
    }
    return isStepCoreValid(step);
  };

  const markerTemplate = (item: any) => {
    const isValid = isStepValid(item);
    const icon = isValid ? "pi pi-check" : item.icon;
    const iconColor = isValid ? "green" : item.iconColor;

    return (
        <div
            className="flex items-center justify-center w-8 h-8 rounded-full"
            style={{ backgroundColor: iconColor }}
        >
          <i className={icon} style={{ color: "white" }} />
        </div>
    );
  };

  const handleSubmit = async (values: KycValues) => {
    setSubmitting(true);
    try {
      const nationalIdSteps = steps.filter((step) =>
          step.uploadKey.startsWith("NATIONAL_ID")
      );

      const missingFiles = nationalIdSteps.filter(
          (step) => !filePreviews[step.uploadKey]
      );

      if (missingFiles.length > 0) {
        toastRef.current?.show({
          severity: "error",
          summary: "Missing Files",
          detail: "Please upload both FRONT and BACK sides of the NATIONAL ID.",
          life: 3000,
        });
        setSubmitting(false);
        return;
      }

      const missingFields = steps.filter((step) => {
        if (step.uploadKey.endsWith("_BACK")) {
          return false;
        }
        if (!filePreviews[step.uploadKey]) {
          return false;
        }
        if (step.requiresNumber && !step.documentNumber?.trim()) {
          return true;
        }
        if (step.requiresExpiry && !step.expiryDate) {
          return true;
        }
        return false;
      });

      if (missingFields.length > 0) {
        const fieldNames = missingFields.map((f) => f.status).join(", ");
        toastRef.current?.show({
          severity: "error",
          summary: "Missing Information",
          detail: `Please fill in document number and/or expiry date for: ${fieldNames}`,
          life: 5000,
        });
        setSubmitting(false);
        return;
      }

      const expiredDates = steps.filter((step) => {
        if (step.uploadKey.endsWith("_BACK")) {
          return false;
        }
        if (!filePreviews[step.uploadKey]) {
          return false;
        }
        if (step.requiresExpiry && step.expiryDate) {
          const expiryDate = new Date(step.expiryDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return expiryDate < today;
        }
        return false;
      });

      if (expiredDates.length > 0) {
        const fieldNames = expiredDates.map((f) => f.status).join(", ");
        toastRef.current?.show({
          severity: "error",
          summary: "Invalid Expiry Date",
          detail: `Expiry date must be in the future for: ${fieldNames}`,
          life: 5000,
        });
        setSubmitting(false);
        return;
      }

      const uploadedFilesArray: DocumentFile[] = [];
      const documentsMetadataArray: DocumentMetadata[] = [];

      for (const step of steps) {
        const documentTypeCode = step.uploadKey.startsWith("NATIONAL_ID")
            ? "NATIONAL_ID"
            : step.uploadKey;
        const fileUrl = filePreviews[step.uploadKey];

        if (fileUrl) {
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch file from ${fileUrl}`);
          }

          const blob = await response.blob();
          let extension = "pdf";

          if (blob.type === "application/pdf") {
            extension = "pdf";
          } else if (blob.type === "image/png") {
            extension = "png";
          } else if (blob.type === "image/jpeg") {
            extension = "jpg";
          }

          const file = new File([blob], `${step.uploadKey}.${extension}`, {
            type: blob.type,
          });

          uploadedFilesArray.push({ files: file });

          const expiryDate =
              step.uploadKey.endsWith("_BACK")
                  ? steps.find((s) => s.uploadKey === "NATIONAL_ID_FRONT")?.expiryDate
                  : step.expiryDate || null;

          const documentNumber =
              step.uploadKey.endsWith("_BACK")
                  ? steps.find((s) => s.uploadKey === "NATIONAL_ID_FRONT")?.documentNumber
                  : step.documentNumber || "";

          const metadata: DocumentMetadata = {
            doc_type_code: documentTypeCode,
            expiryDate: expiryDate,
            documentNumber: documentNumber,
            side: step.uploadKey.endsWith("_FRONT")
                ? "FRONT"
                : step.uploadKey.endsWith("_BACK")
                    ? "BACK"
                    : null,
          };

          documentsMetadataArray.push(metadata);
        }
      }

      const result = await DocumentService.uploadDocuments(
          uploadedFilesArray,
          documentsMetadataArray
      );

      if (result && result.status === -1) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Error",
          detail: result.message,
          life: 3000,
        });
      } else {
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: result.message,
          life: 3000,
        });
        navigate("/kyc-under-review");
      }
    } catch (error: any) {
      const errorMessage =
          error.response?.data?.message ||
          "An error occurred while submitting the KYC documents.";
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openFileInNewTab = (fileUrl: string) => {
    window.open(fileUrl, "_blank");
  };

  const formik = useFormik({
    initialValues: initialKycValues,
    onSubmit: handleSubmit,
  });

  const validateForm = () => {
    if (steps.length === 0) {
      return false;
    }

    const nationalIdSteps = steps.filter((step) =>
        step.uploadKey.startsWith("NATIONAL_ID")
    );
    const missingNationalIdFiles = nationalIdSteps.filter(
        (step) => !filePreviews[step.uploadKey]
    );
    if (missingNationalIdFiles.length > 0) {
      return false;
    }

    const requiredSteps = steps.filter((step) => step.required);
    const stepsToCheck = requiredSteps.length > 0 ? requiredSteps : steps;
    for (const step of stepsToCheck) {
      if (!filePreviews[step.uploadKey]) {
        return false;
      }
    }

    for (const step of steps) {
      if (step.uploadKey.endsWith("_BACK")) {
        continue;
      }
      if (!filePreviews[step.uploadKey]) {
        continue;
      }
      if (step.requiresNumber && !step.documentNumber?.trim()) {
        return false;
      }
      if (step.requiresExpiry && !step.expiryDate) {
        return false;
      }
      if (step.requiresExpiry && step.expiryDate) {
        const expiryDate = new Date(step.expiryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (expiryDate < today) {
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [steps, filePreviews]);

  return (
      <div className="kyc-form-enterprise w-3/2">
        <Toast ref={toastRef} />
        <div className="flex flex-col items-center">
          <h4 className="text-xl mt-5 mb-1 font-bold">
            Submit Your Company's Documents
          </h4>
          <p className="mb-6 text-slate-500 text-sm">
            Please provide documentation about your business.
          </p>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <Timeline
              value={steps}
              className="w-full gap-2"
              opposite={null}
              marker={markerTemplate}
              content={(item, index) => (
                  <Panel
                      header={
                        <div>
                          {item.status} {item.required && <span className="text-red-500">*</span>}
                        </div>
                      }
                      toggleable
                      collapsed={item.isCollapsed}
                      onToggle={() => togglePanel(index)}
                      headerTemplate={(options) => (
                          <div
                              className="p-panel-header"
                              style={{ display: "flex", justifyContent: "space-between" }}
                          >
                  <span>
                    {item.status} {item.required && <span className="text-red-500">*</span>}
                  </span>
                            <Button
                                icon={`pi ${item.isCollapsed ? "pi pi-plus" : "pi pi-minus"}`}
                                onClick={() => togglePanel(index)}
                                className="p-button-text"
                                style={{ color: "#274081", fontSize: "20px" }}
                                type="button"
                            />
                          </div>
                      )}
                  >
                    {!item.isCollapsed && (
                        <div>
                          <span>{item.description}</span>
                          <FileUpload
                              ref={fileUploadRef}
                              name={item.uploadKey}
                              customUpload
                              accept="application/pdf,image"
                              maxFileSize={4000000}
                              invalidFileSizeMessageSummary=""
                              invalidFileSizeMessageDetail=""
                              uploadHandler={(e) => handleFileUpload(e, item.uploadKey)}
                              onSelect={(e) => handleFileSelect(e, item.uploadKey)}
                              headerTemplate={headerTemplate}
                              onClear={() => handleFileClear(item.uploadKey)}
                          />
                          {filePreviews[item.uploadKey] ? (
                              <div
                                  style={{
                                    marginTop: "10px",
                                    cursor: "pointer",
                                    borderRadius: "10px",
                                  }}
                                  onClick={() =>
                                      openFileInNewTab(filePreviews[item.uploadKey] as string)
                                  }
                              >
                                {item.uploadKey.startsWith("image") ? (
                                    <img
                                        src={filePreviews[item.uploadKey]}
                                        alt="Preview"
                                        style={{ width: "100px", height: "100px" }}
                                    />
                                ) : (
                                    <div className="text-primary-500 mt-2">
                                      {fileNames[item.uploadKey]}
                                    </div>
                                )}
                              </div>
                          ) : (
                              <div className="h-4" /> // Empty placeholder, no persistent message
                          )}

                          {(item.requiresNumber || item.requiresExpiry) && !item.uploadKey.endsWith("_BACK") && (
                              <div className="flex gap-4 mt-3">
                                {item.requiresNumber && (
                                    <div className="flex-1">
                                      <label htmlFor={`documentNumber-${item.uploadKey}`} className="block">
                                        Document Number <span className="text-red-500">*</span>
                                      </label>
                                      <InputText
                                          id={`documentNumber-${item.uploadKey}`}
                                          type="text"
                                          className="w-full border border-gray-300 rounded p-1 h-10"
                                          value={item.documentNumber || ""}
                                          onChange={(e: { target: { value: any } }) => {
                                            setSteps((prevSteps) =>
                                                prevSteps.map((step, i) =>
                                                    i === index
                                                        ? { ...step, documentNumber: e.target.value }
                                                        : step
                                                )
                                            );
                                          }}
                                      />
                                    </div>
                                )}

                                {item.requiresExpiry && (
                                    <div className="flex-1">
                                      <label htmlFor={`expiryDate-${item.uploadKey}`} className="block">
                                        Expiry Date <span className="text-red-500">*</span>
                                      </label>
                                      <Calendar
                                          id={`expiryDate-${item.uploadKey}`}
                                          className="w-full border border-gray-300 rounded p-1 h-10"
                                          value={item.expiryDate ? new Date(item.expiryDate) : null}
                                          onChange={(e: { value: any }) => {
                                            const formattedDate = e.value ? moment(e.value).format("YYYY-MM-DD") : "";
                                            setSteps((prevSteps) =>
                                                prevSteps.map((step, i) =>
                                                    i === index
                                                        ? { ...step, expiryDate: formattedDate }
                                                        : step
                                                )
                                            );
                                          }}
                                          showIcon
                                          dateFormat="yy-mm-dd"
                                      />
                                    </div>
                                )}
                              </div>
                          )}
                        </div>
                    )}
                  </Panel>
              )}
          />
          <div className="text-sm text-gray-500 mt-2">
            <span className="text-red-500">*</span> Required fields
          </div>
          <Button
              label="Submit KYC"
              type="submit"
              loading={submitting}
              className={`w-full submit-btn
          p-2 mt-6 font-bold text-xl rounded
          text-white ${isFormValid && !submitting ? 'bg-primary-500 hover:bg-orange-600' : 'bg-gray-400 cursor-not-allowed'}
          duration-200 text-center`}
              disabled={submitting || !isFormValid}
          />
        </form>
      </div>
  );
};

export default BusinessKycForm;
